import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCcw, Trash2, MessageSquare } from "lucide-react";

export function PendingInvitesCard({ invites, reload }) {
  const [twilioReady, setTwilioReady] = useState(false);
  useEffect(() => {
    api.get("/integrations/twilio/status").then(({ data }) => setTwilioReady(!!data?.configured)).catch(() => setTwilioReady(false));
  }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invitation?")) return;
    await api.delete(`/invites/${id}`);
    toast.success("Invite revoked");
    reload();
  };
  const resend = async (id) => {
    try {
      await api.post(`/invites/${id}/resend`);
      toast.success("Invitation email re-sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend");
    }
  };
  const sendSms = async (id, email) => {
    if (!twilioReady) {
      toast.error("Twilio isn't configured yet. Add TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_FROM_NUMBER to backend/.env and restart to enable SMS invites.");
      return;
    }
    const phone = window.prompt(`Send invite SMS to ${email}\n\nEnter their phone in E.164 format (e.g. +15551234567):`);
    if (!phone) return;
    try {
      const { data } = await api.post(`/invites/${id}/send-sms`, { phone: phone.trim() });
      toast.success(`SMS sent to ${data.phone}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send SMS");
    }
  };

  return (
    <section>
      <h2 className="text-lg font-bold mb-3 text-slate-200">Pending &amp; recent invitations</h2>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#0b0d12] border-b border-[#2a3447] text-slate-400 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Role</th>
              <th className="text-left px-6 py-3">Sent by</th>
              <th className="text-left px-6 py-3">Status</th>
              <th className="text-left px-6 py-3">Expires</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {invites.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-slate-500 text-sm">
                  No invitations yet. Click <strong className="text-slate-300">Send Invite</strong> to add your first teammate.
                </td>
              </tr>
            )}
            {invites.map((inv) => (
              <tr key={inv.id} className="border-b border-[#1a202c]" data-testid={`invite-row-${inv.id}`}>
                <td className="px-6 py-3 font-semibold">{inv.email}</td>
                <td className="px-6 py-3 text-slate-300">{inv.role}</td>
                <td className="px-6 py-3 text-slate-400">{inv.invited_by_name}</td>
                <td className="px-6 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    inv.status === "pending" ? "bg-emerald-900/40 text-emerald-300" :
                    inv.status === "accepted" ? "bg-slate-800 text-slate-300" :
                    inv.status === "revoked" ? "bg-rose-900/40 text-rose-300" :
                    "bg-amber-900/40 text-amber-300"
                  }`}>{inv.status}</span>
                </td>
                <td className="px-6 py-3 text-slate-500 text-xs">{new Date(inv.expires_at).toLocaleDateString()}</td>
                <td className="px-6 py-3 text-right space-x-2">
                  {inv.status === "pending" && (
                    <>
                      <button
                        onClick={() => sendSms(inv.id, inv.email)}
                        data-testid={`invite-sms-${inv.id}`}
                        className={`${twilioReady ? "text-slate-500 hover:text-[color:var(--accent)]" : "text-slate-700 hover:text-slate-500"}`}
                        title={twilioReady ? "Send as SMS (Twilio)" : "SMS unavailable — Twilio not configured"}
                      >
                        <MessageSquare size={16} />
                      </button>
                      <button onClick={() => resend(inv.id)} data-testid={`invite-resend-${inv.id}`} className="text-slate-500 hover:text-[color:var(--accent)]" title="Resend email"><RefreshCcw size={16} /></button>
                      <button onClick={() => revoke(inv.id)} data-testid={`invite-revoke-${inv.id}`} className="text-slate-500 hover:text-[#ff3b5c]" title="Revoke"><Trash2 size={16} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default PendingInvitesCard;
